import { Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import ObjectiveParticipantChip from './ObjectiveParticipantChip';
import { PARTICIPANT_ATTRIBUTE_ICONS, participantAttributeKey } from '../participantAttributes';

import type { IndexedParticipantAttribute } from '../participantAttributes';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';

import { ObjectiveParticipantAttributeType } from '@/constants/ObjectiveParticipantAttributeType';

/** A manager's chip stands for the people they manage, where the option names the manager. */
const MANAGER_CHIP_ICON = 'users-profile-2user';

interface ObjectiveAttributeChipProps {
    attribute: ObjectiveParticipantAttribute;
    /** Undefined until the company has answered; a failed read is an answer. */
    attributesByKey: ReadonlyMap<string, IndexedParticipantAttribute> | undefined;
    /** How many people the value brings in, once counted within the reader's reach. */
    employeeCount: number | undefined;
    onRemove?: () => void;
}

/** The chip of a company value whose members take part, named as the reader knows the value. */
export default function ObjectiveAttributeChip({
    attribute,
    attributesByKey,
    employeeCount,
    onRemove,
}: ObjectiveAttributeChipProps) {
    const { t } = useTranslation();

    /** The chip's name and its remove button's name, which reads as a sentence around it. */
    const nameChip = (): { name: string; removeLabel: string } => {
        const removing = (name: string) => ({
            name,
            removeLabel: t('objectives.form.participantPicker.removeAttribute', { name }),
        });

        // A failed read says so: "unavailable" would read as a value removed from the company.
        if (!attributesByKey) {
            return removing(t('objectives.form.participantPicker.attributeLoadFailed'));
        }

        const valueName = attributesByKey.get(participantAttributeKey(attribute))?.option.name;
        if (!valueName) {
            return removing(t('objectives.form.participantPicker.unavailableAttribute'));
        }

        if (attribute.type !== ObjectiveParticipantAttributeType.MANAGER) {
            return removing(valueName);
        }

        // A manager's chip names the people it brings in: the manager's name alone reads as the
        // manager picked as an employee.
        return {
            name: t('objectives.form.participantPicker.managerEmployees', { name: valueName }),
            removeLabel: t('objectives.form.participantPicker.removeManagerEmployees', {
                name: valueName,
            }),
        };
    };

    const key = participantAttributeKey(attribute);
    const { name, removeLabel } = nameChip();

    return (
        <ObjectiveParticipantChip
            leadingInset={'icon'}
            leading={
                <Icon
                    name={
                        attribute.type === ObjectiveParticipantAttributeType.MANAGER
                            ? MANAGER_CHIP_ICON
                            : PARTICIPANT_ATTRIBUTE_ICONS[attribute.type]
                    }
                    size={'sm'}
                    colorClassName={'text-obj-muted'}
                />
            }
            name={name}
            trailing={
                employeeCount !== undefined && (
                    <span className={'text-obj-muted'}>
                        <span aria-hidden={'true'}>{'· '}</span>
                        {t('objectives.form.participantPicker.memberCount', {
                            count: employeeCount,
                        })}
                    </span>
                )
            }
            removal={
                onRemove && {
                    label: removeLabel,
                    onRemove,
                    dataTestId: `objective-attribute-remove-${key}`,
                }
            }
            dataTestId={`objective-attribute-selected-${key}`}
        />
    );
}
